import {FlatTreeControl} from '@angular/cdk/tree';
import {Component, Injectable, ViewChild} from '@angular/core';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {BehaviorSubject, Observable, of as observableOf} from 'rxjs';

/**
 * File node data with nested structure.
 * Each node has a filename, and a type or a list of children.
 */
export class FileNode {
  children: FileNode[];
  label: string;
  routeName: string;
}

/** Flat node with expandable and level information */
export class FileFlatNode {
  constructor(
    public expandable: boolean, public label: string, public level: number, public routeName: string) {}
}

/**
 * The file structure tree data in string. The data could be parsed into a Json object
 */
const TREE_DATA = JSON.stringify({
  Dashboard: {route:'dashboard-route',label:'Dashboard'},
  SalesContracts:{route:'sales-contracts-route',label:'Sales Contracts'},
  Reports: {
    DeliveryReport: {route:'delivery-report-route',label:'Delivery Report'},
    HaulierReport: {route:'haulier-report-route', label:'Haulier Report'},
  },
  Configuration: {
    EmailTemplates: {route: 'configure-email-template-route', label:'Email Templates'},
    Countries: {route:'configure-countries-route'},
    Users: { route: "/configuration/users" }
  }
});

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
@Injectable()
export class FileDatabase {
  dataChange = new BehaviorSubject<FileNode[]>([]);

  get data(): FileNode[] { return this.dataChange.value; }

  constructor() {
    this.initialize();
  }

  initialize() {
    // Parse the string to json object.
    const dataObject = JSON.parse(TREE_DATA);

    // Build the tree nodes from Json object. The result is a list of `FileNode` with nested
    //     file node as children.
    const data = this.buildFileTree(dataObject, 0);

    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `FileNode`.
   */
  buildFileTree(obj: object, level: number): FileNode[] {
    return Object.keys(obj).reduce<FileNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new FileNode();
      node.label = key;

      if (value != null) {
        if (typeof value === 'object') {
          if (value.route != null)
          {
            node.routeName = value.route;
          }
          if (value.label != null)
          {
            node.label = value.label;
          }
          let childCount = 0;
          for(let child in Object.keys(value)){
            let childName = Object.keys(value)[child];
            if ((childName != "route") &&  (childName != "label") )
              childCount++;
          };
          if (childCount > 0){
            node.children = this.buildFileTree(value, level + 1);
          }
        } 
      }
      return accumulator.concat(node);
    }, []);
  }
}

/**
 * @title Tree with flat nodes
 */
@Component({ standalone: false,
  selector: 'tree-menu',
  templateUrl: 'tree-menu.component.html',
  styleUrls: ['tree-menu.component.scss'],
  providers: [FileDatabase]
})
export class TreeMenuComponent {
  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;

  constructor(database: FileDatabase) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel,
      this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    database.dataChange.subscribe(data => this.dataSource.data = data);
  }

  transformer = (node: FileNode, level: number) => {
    return new FileFlatNode(!!node.children, node.label, level, node.routeName);
  }

  private _getLevel = (node: FileFlatNode) => node.level;

  private _isExpandable = (node: FileFlatNode) => node.expandable;

  private _getChildren = (node: FileNode): Observable<FileNode[]> => observableOf(node.children);

  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  @ViewChild('tree') tree;

  ngAfterViewInit() {
    this.tree.treeControl.expandAll();
  }  
}


/**  Copyright 2018 Google Inc. All Rights Reserved.
    Use of this source code is governed by an MIT-style license that
    can be found in the LICENSE file at http://angular.io/license */
